import { useEffect, useRef, useState } from 'react';
import {
  loadPdfDocument,
  rasterizePdfThumbnails,
  type LoadedPdfDocument,
  type PdfPageThumbnail,
} from '../helpers/pdf-rasterize.helpers';
import { isPdfRenderingCancelled } from '../helpers/pdf-page-cleanup.helpers';

type UsePdfDocumentOptions = {
  open: boolean;
  file: File;
  onError: (error: unknown) => void;
};

/**
 * Load one PDF document for the import wizard (thumbs + live preview).
 * Increments {@link UsePdfDocumentResult.sessionId} after a successful thumb pass.
 */
export const usePdfDocument = ({
  open,
  file,
  onError,
}: UsePdfDocumentOptions): {
  loadedPdf: LoadedPdfDocument | undefined;
  thumbnails: PdfPageThumbnail[];
  loadingThumbnails: boolean;
  sessionId: number;
} => {
  const [loadedPdf, setLoadedPdf] = useState<LoadedPdfDocument | undefined>();
  const [thumbnails, setThumbnails] = useState<PdfPageThumbnail[]>([]);
  const [loadingThumbnails, setLoadingThumbnails] = useState(true);
  const [sessionId, setSessionId] = useState(0);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let loaded: LoadedPdfDocument | undefined;
    setLoadingThumbnails(true);
    setThumbnails([]);
    setLoadedPdf(undefined);
    void loadPdfDocument(file)
      .then(async (next) => {
        if (cancelled) {
          await next.destroy();
          return;
        }
        loaded = next;
        // Rasterize before exposing `loadedPdf` so live preview cannot cancel thumb paints.
        const { thumbnails: thumbs } = await rasterizePdfThumbnails(
          next.doc,
          next.pageNames,
        );
        if (cancelled) return;
        setLoadedPdf(next);
        setThumbnails(thumbs);
        setSessionId((id) => id + 1);
      })
      .catch((error: unknown) => {
        if (cancelled || isPdfRenderingCancelled(error)) return;
        onErrorRef.current(error);
      })
      .finally(() => {
        if (!cancelled) setLoadingThumbnails(false);
      });
    return () => {
      cancelled = true;
      void loaded?.destroy();
      setLoadedPdf(undefined);
    };
  }, [file, open]);

  return { loadedPdf, thumbnails, loadingThumbnails, sessionId };
};
